import React from 'react';
import { DropdownMenu, TagGeneral, Text } from '@spark';
import { TaskType, isGenerativeTask, getGenerativeOutputNoun } from '@/shared/domain/taskTypes';

export const ENTIRE_RESPONSE_OPTION_LABEL = 'Entire response';

/** Read-only "Extracted X" value box (Figma Output card mapping). */
const ExtractedValue = ({ label, value, noun }) => (
    <div className="model-setup-map-extracted">
        <Text variant="base-medium" color="primary" as="div" className="fs-unmask">
            {label}
        </Text>
        <div className="model-setup-map-extracted__value">
            <Text variant="base-regular" color="primary" as="div">
                {value || `No ${noun} found`}
            </Text>
        </div>
    </div>
);

/**
 * Generative (summarization / content generation) mapping summary
 * (Figma .ModelSetup-CompareReadyMapped): "Using [field] as the {noun}." plus
 * the extracted value. Mapping happens by clicking a value in the raw JSON
 * output; content generation defaults to the entire response.
 */
const SummarizationSummary = ({
    taskType,
    responseType,
    rawResponse,
    parsedJson,
    normalizedJsonLabelField,
    jsonLabelFieldOptions,
    setSelectedJsonLabelField,
    selectedJsonTokens,
    getJsonValueByPath,
    formatNerAssignedValue,
    parserOutput, // For custom mode / validation results
    readOnly = false,
}) => {
    if (!isGenerativeTask(taskType)) {
        return null;
    }

    const noun = getGenerativeOutputNoun(taskType);
    const isContentGeneration = taskType === TaskType.contentGeneration;

    // Handle Parser Output Mode (Custom / Validation Result)
    if (parserOutput) {
        let summaryText = '';
        if (parserOutput && parserOutput.response) {
            summaryText = String(parserOutput.response);
        } else if (typeof parserOutput === 'string') {
            summaryText = parserOutput;
        }
        return (
            <div className="response-step-card__summarization-summary model-setup-map-summary">
                <ExtractedValue label={`Extracted ${noun}`} value={summaryText} noun={noun} />
            </div>
        );
    }

    // Text response type: the whole response is the value.
    if (responseType === 'text') {
        if (!rawResponse) {
            return null;
        }
        return (
            <div className="response-step-card__summarization-summary model-setup-map-summary">
                <ExtractedValue label={`Extracted ${noun}`} value={rawResponse} noun={noun} />
            </div>
        );
    }

    if (responseType !== 'json') {
        return null;
    }

    // Content generation: the explicit field pick alone decides (token
    // matches are not required, e.g. object-valued fields); no pick means
    // the entire returned object.
    const hasJsonSelection = isContentGeneration
        ? normalizedJsonLabelField != null
        : normalizedJsonLabelField && Object.keys(selectedJsonTokens).length > 0;
    const isEntireResponse = isContentGeneration && !hasJsonSelection;

    if (isEntireResponse && !parsedJson) {
        return null;
    }

    // Unmapped (non-content-generation): guide the click-to-map interaction.
    if (!isEntireResponse && !hasJsonSelection) {
        const fieldOptions = (jsonLabelFieldOptions || []).map(({ value, label }) => ({
            value,
            label: value || label,
        }));
        return (
            <div className="response-step-card__summarization-summary model-setup-map-summary fs-unmask">
                <Text variant="detail-regular" color="secondary" as="div">
                    Click a value to map it as the prediction.
                </Text>
                {fieldOptions.length > 0 && !readOnly && (
                    <div className="model-setup-map-summary__fallback-select">
                        <DropdownMenu
                            showLabel={false}
                            placeholder={`Select ${noun} field`}
                            items={fieldOptions.map((opt) => ({
                                label: String(opt.label),
                                state: opt.value === normalizedJsonLabelField ? 'selected' : 'default',
                            }))}
                            onSelect={(item, index) => {
                                const opt = fieldOptions[index];
                                if (opt) setSelectedJsonLabelField(opt.value);
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }

    const selectedFieldDisplayValue = isEntireResponse
        ? formatNerAssignedValue(parsedJson)
        : formatNerAssignedValue(getJsonValueByPath(parsedJson, normalizedJsonLabelField));

    return (
        <div className="response-step-card__summarization-summary model-setup-map-summary">
            <div className="model-setup-map-confirm fs-unmask">
                <Text variant="detail-regular" color="primary" as="span">
                    Using
                </Text>
                <TagGeneral
                    label={isEntireResponse ? ENTIRE_RESPONSE_OPTION_LABEL : String(normalizedJsonLabelField)}
                    variant={readOnly || isEntireResponse ? 'default' : 'closeable'}
                    onClose={readOnly || isEntireResponse
                        ? undefined
                        : () => setSelectedJsonLabelField(isContentGeneration ? null : '')}
                />
                <Text variant="detail-regular" color="primary" as="span">
                    as the {noun}.
                </Text>
            </div>
            <ExtractedValue
                label={`Extracted ${noun}`}
                value={selectedFieldDisplayValue}
                noun={noun}
            />
        </div>
    );
};

export default SummarizationSummary;
